import { CheckCircle2, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { SwapWallet } from "../services/swap.service";
import type { SwapSuccessState } from "../types/swaps-api.types";

interface SwapSuccessScreenProps {
  /** Pre-swap `balance_available`, captured from the runtime wallets before submitting. */
  oldBalances: SwapSuccessState["oldBalances"];
  /** Post-swap wallets, straight from the swap response — authoritative, no client arithmetic. */
  newBalances: SwapSuccessState["newBalances"];
  summary: SwapSuccessState["summary"];
  onNewSwap: () => void;
}

interface BalanceCardProps {
  wallet: SwapWallet;
  previousBalance: string | null;
  /** The exact amount the swap moved, from the response — never a client-side subtraction of balances. */
  delta: string;
  direction: "debit" | "credit";
  label: string;
  previousLabel: string;
  currentLabel: string;
  changeLabel: string;
}

const BalanceCard: React.FC<BalanceCardProps> = ({
  wallet,
  previousBalance,
  delta,
  direction,
  label,
  previousLabel,
  currentLabel,
  changeLabel,
}) => {
  const isDebit = direction === "debit";
  return (
    <ColocaSurface variant="panel" padding="lg">
      <div className="mb-4">
        <h3 className="font-semibold text-foreground">{wallet.currency}</h3>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      </div>

      <div className="space-y-2">
        {previousBalance !== null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{previousLabel}:</span>
            <ColocaAmountDisplay
              value={previousBalance}
              currency={wallet.currency}
              size="sm"
              minimumFractionDigits={2}
              maximumFractionDigits={8}
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">{currentLabel}:</span>
          <div className="flex items-center gap-2">
            {isDebit ? (
              <TrendingDown className="h-4 w-4 text-destructive" aria-hidden="true" />
            ) : (
              <TrendingUp className="h-4 w-4 text-success" aria-hidden="true" />
            )}
            <ColocaAmountDisplay
              value={wallet.balance.available}
              currency={wallet.currency}
              size="md"
              minimumFractionDigits={2}
              maximumFractionDigits={8}
            />
          </div>
        </div>

        <div className="border-t border-border pt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{changeLabel}:</span>
            <span className={cn("text-xs font-medium", isDebit ? "text-destructive" : "text-success")}>
              {isDebit ? "−" : "+"}
              <ColocaAmountDisplay
                value={delta}
                currency={wallet.currency}
                size="xs"
                minimumFractionDigits={2}
                maximumFractionDigits={8}
              />
            </span>
          </div>
        </div>
      </div>
    </ColocaSurface>
  );
};

export const SwapSuccessScreen: React.FC<SwapSuccessScreenProps> = ({
  oldBalances,
  newBalances,
  summary,
  onNewSwap,
}) => {
  const { t } = useTranslations("swaps");

  return (
    // No max-width of its own: the page already sets the column, and a narrower success screen would make
    // the layout jump the moment the swap completes.
    <div className="space-y-6">
      <div className="space-y-4 text-center">
        <div className="flex justify-center">
          <span
            className="grid h-16 w-16 place-items-center rounded-full bg-success/10 text-success"
            aria-hidden="true"
          >
            <CheckCircle2 className="h-8 w-8" />
          </span>
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">{t("success_title")}</h2>
        <p className="text-sm text-muted-foreground">{t("success_description")}</p>
      </div>

      <div className="space-y-4">
        {newBalances.origin && summary && (
          <BalanceCard
            wallet={newBalances.origin}
            previousBalance={oldBalances.origin}
            delta={summary.total_debited}
            direction="debit"
            label={t("origin_label")}
            previousLabel={t("previous_balance")}
            currentLabel={t("current_balance")}
            changeLabel={t("change")}
          />
        )}

        {newBalances.destination && summary && (
          <BalanceCard
            wallet={newBalances.destination}
            previousBalance={oldBalances.destination}
            delta={summary.amount_received}
            direction="credit"
            label={t("destination_label")}
            previousLabel={t("previous_balance")}
            currentLabel={t("current_balance")}
            changeLabel={t("change")}
          />
        )}
      </div>

      {summary && (
        <ColocaSurface variant="muted" padding="md">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("conversion_rate")}:</span>
            <span className="text-sm font-semibold text-foreground">
              1 {summary.origin_currency} ={" "}
              <ColocaAmountDisplay
                value={summary.effective_rate}
                currency={summary.destination_currency}
                size="sm"
                minimumFractionDigits={2}
                maximumFractionDigits={8}
              />
            </span>
          </div>
        </ColocaSurface>
      )}

      <ColocaButton variant="primary" onClick={onNewSwap} className="w-full">
        {t("make_another_swap")}
      </ColocaButton>
    </div>
  );
};
